
import React, { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Search, RefreshCcw, CheckCircle, XCircle } from 'lucide-react';
import {
  createTicket,
  getUserTickets,
  replyToTicket,
  closeTicket,
  reopenTicket,
  getTicketPermissions,
  formatCentralTime,
  SupportTicket as ServiceSupportTicket
} from '@/utils/supportTicketService';
import { useAuth } from '@/context/SupabaseAuthContext';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { UserRole } from '@/types/userRole';

// Define a local interface that matches the component's expectations
interface SupportTicket {
  id: string;
  subject: string;
  status: 'open' | 'pending' | 'closed';
  priority: 'low' | 'medium' | 'high';
  createdAt: Date;
  userId: string;
  userName: string;
  userEmail: string;
  instanceId?: string;
  content: string;
}

interface TicketReply {
  id: string;
  ticketId: string;
  userId: string;
  userName: string;
  userRole: UserRole;
  content: string;
  createdAt: Date;
}

const SupportManagementPage: React.FC = () => {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const { user, currentInstanceId } = useSession();
  const userId = user?.id;
  const role = user?.role;
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  useEffect(() => {
    if (!userId) return;

    const fetchTickets = async () => {
      try {
        const userTickets = await getUserTickets(userId);
        // Convert service tickets to component tickets
        const convertedTickets = userTickets.map(ticket => ({
          id: ticket.id,
          subject: ticket.subject,
          status: ticket.status === 'resolved' ? 'closed' as const : ticket.status as any,
          priority: ticket.priority,
          createdAt: ticket.createdAt,
          userId: ticket.userId,
          userName: ticket.userName,
          userEmail: ticket.userEmail,
          instanceId: ticket.instanceId,
          content: ticket.content || ticket.message
        }));
        setTickets(convertedTickets);
      } catch (error) {
        console.error('Error fetching tickets:', error);
        toast({
          title: 'Error',
          description: 'Failed to load support tickets',
          variant: 'destructive',
        });
      }
    };

    fetchTickets();
  }, [userId, toast]);

  const isAdminOrSuperAdmin = role === 'admin' || role === 'superadmin';
  
  const filteredTickets = useMemo(() => {
    if (!tickets) return [];
    
    let filtered = [...tickets];
    
    // Filter tickets based on role
    if (role === 'superadmin') {
      // SuperAdmin sees all tickets
    } else if (role === 'admin') {
      // Admins see all tickets from their instance
      filtered = filtered.filter(ticket => ticket.instanceId === currentInstanceId);
    } else {
      // Regular users only see their own tickets
      filtered = filtered.filter(ticket => ticket.userId === userId);
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }
    
    // Filter by priority
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }
    
    // Filter by search query
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.subject.toLowerCase().includes(lowerCaseQuery) ||
        ticket.content.toLowerCase().includes(lowerCaseQuery) ||
        ticket.userName.toLowerCase().includes(lowerCaseQuery) ||
        ticket.userEmail.toLowerCase().includes(lowerCaseQuery)
      );
    }
    
    return filtered;
  }, [tickets, statusFilter, priorityFilter, searchQuery, role, userId, currentInstanceId]);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (userId) {
        const updatedTickets = await getUserTickets(userId);
        // Convert service tickets to component tickets
        const convertedTickets = updatedTickets.map(ticket => ({
          id: ticket.id,
          subject: ticket.subject,
          status: ticket.status === 'resolved' ? 'closed' as const : ticket.status as any,
          priority: ticket.priority,
          createdAt: ticket.createdAt,
          userId: ticket.userId,
          userName: ticket.userName,
          userEmail: ticket.userEmail,
          instanceId: ticket.instanceId,
          content: ticket.content || ticket.message
        }));
        setTickets(convertedTickets);
        toast({
          title: 'Tickets Refreshed',
          description: 'Support tickets have been updated',
        });
      }
    } catch (error) {
      console.error('Error refreshing tickets:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh support tickets',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handleStatusChange = async (ticketId: string, newStatus: 'open' | 'pending' | 'closed') => {
    try {
      let success = false;
      if (newStatus === 'closed') {
        await closeTicket(ticketId);
        success = true;
      } else if (newStatus === 'open') {
        await reopenTicket(ticketId);
        success = true;
      }
      
      if (success) {
        setTickets(prevTickets =>
          prevTickets ? prevTickets.map(ticket =>
            ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
          ) : []
        );
        toast({
          title: 'Ticket Updated',
          description: `Ticket status changed to ${newStatus}`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update ticket status',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update ticket status',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <div className="container mx-auto py-6">
      <h1 className="text-2xl font-bold mb-6">Support Ticket Management</h1>
      
      <div className="mb-4 flex flex-col md:flex-row gap-4">
        <Input
          type="text"
          placeholder="Search tickets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="md:w-1/3"
        />
        
        <Select onValueChange={setStatusFilter} defaultValue={statusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        
        <Select onValueChange={setPriorityFilter} defaultValue={priorityFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
          </SelectContent>
        </Select>
        
        <Button onClick={handleRefresh} disabled={isRefreshing}>
          {isRefreshing ? (
            <>
              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Refresh Tickets
            </>
          )}
        </Button>
      </div>
      
      {isAdminOrSuperAdmin && (
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <Button onClick={() => navigate('/admin/support/new')}>
            Create New Ticket
          </Button>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTickets.map(ticket => (
              <TableRow key={ticket.id}>
                <TableCell>{ticket.subject}</TableCell>
                <TableCell>
                  {ticket.status === 'open' && <span className="text-green-500">Open</span>}
                  {ticket.status === 'pending' && <span className="text-yellow-500">Pending</span>}
                  {ticket.status === 'closed' && <span className="text-gray-500">Closed</span>}
                </TableCell>
                <TableCell>{ticket.priority}</TableCell>
                <TableCell>{formatCentralTime(ticket.createdAt)}</TableCell>
                <TableCell>
                  <Button variant="secondary" size="sm" onClick={() => navigate(`/support/ticket/${ticket.id}`)}>
                    View Ticket
                  </Button>
                  {isAdminOrSuperAdmin && ticket.status !== 'closed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStatusChange(ticket.id, 'closed')}
                      className="ml-2"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Close
                    </Button>
                  )}
                  {isAdminOrSuperAdmin && ticket.status === 'closed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStatusChange(ticket.id, 'open')}
                      className="ml-2"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Reopen
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredTickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No tickets found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SupportManagementPage;
